import React from 'react';
import { Server, RefreshCw } from 'lucide-react';

export default function WorkerCard({ celeryWorkers, remoteWorkers, workerActionLoading, onWorkerAction, onRefresh, formatNumber }) {
  return (
    <div className="bg-gray-900 border border-purple-500/30 rounded-xl p-4 lg:p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-purple-400 font-bold text-base lg:text-lg flex items-center gap-2">
          <Server className="w-5 h-5" /> WORKERS
          <span className="text-xs bg-purple-600/30 px-2 py-0.5 rounded-full">
            {celeryWorkers.filter(w => w.online).length} online
          </span>
        </h3>
        <button onClick={onRefresh} className="text-gray-400 hover:text-purple-400 transition">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-3">
        {/* Master Worker */}
        {celeryWorkers.filter(w => w.is_master).map(w => (
          <div key={w.name} className="bg-black/30 rounded-xl p-3 border border-cyan-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${w.online ? 'bg-green-400 animate-pulse' : 'bg-red-500'}`}></div>
                <div>
                  <div className="text-white font-medium text-sm">MASTER</div>
                  <div className="text-gray-500 text-xs">{w.name}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="text-center">
                  <div className="text-gray-500">Tasks</div>
                  <div className="text-cyan-400 font-bold">{w.active_tasks}</div>
                </div>
                <div className="text-center">
                  <div className="text-gray-500">Conc.</div>
                  <div className="text-cyan-400 font-bold">{w.concurrency}</div>
                </div>
                <div className="text-center">
                  <div className="text-gray-500">Done</div>
                  <div className="text-cyan-400 font-bold">{formatNumber(w.total_completed)}</div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Remote Workers */}
        {celeryWorkers.filter(w => !w.is_master).map(w => {
          const remote = remoteWorkers.find(r => r.name === 'pm-worker01') || {};
          return (
            <div key={w.name} className="bg-black/30 rounded-xl p-3 border border-purple-500/20">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${w.online ? 'bg-green-400 animate-pulse' : 'bg-red-500'}`}></div>
                  <div>
                    <div className="text-white font-medium text-sm">PM-WORKER01</div>
                    <div className="text-gray-500 text-xs">{w.name} - 10.0.0.2</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="text-center">
                    <div className="text-gray-500">Tasks</div>
                    <div className="text-purple-400 font-bold">{w.active_tasks}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500">Conc.</div>
                    <div className="text-purple-400 font-bold">{w.concurrency}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500">Done</div>
                    <div className="text-purple-400 font-bold">{formatNumber(w.total_completed)}</div>
                  </div>
                </div>
              </div>

              {/* Remote system stats */}
              {remote.online && (
                <div className="grid grid-cols-5 gap-2 mb-2">
                  <div className="bg-gray-800/50 rounded-lg px-2 py-1 text-center">
                    <div className="text-gray-500 text-xs">CPU</div>
                    <div className="text-xs font-bold text-blue-400">{remote.cpu || 0}%</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg px-2 py-1 text-center">
                    <div className="text-gray-500 text-xs">RAM</div>
                    <div className={`text-xs font-bold ${(remote.ram_percent || 0) > 80 ? 'text-red-400' : 'text-green-400'}`}>
                      {remote.ram_percent || 0}%
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg px-2 py-1 text-center">
                    <div className="text-gray-500 text-xs">Chrome</div>
                    <div className="text-xs font-bold text-purple-400">{remote.chrome || 0}</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg px-2 py-1 text-center">
                    <div className="text-gray-500 text-xs">Load</div>
                    <div className="text-xs font-bold text-yellow-400">{remote.load || 0}</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg px-2 py-1 text-center">
                    <div className="text-gray-500 text-xs">Disk</div>
                    <div className="text-xs font-bold text-orange-400">{remote.disk || 0}%</div>
                  </div>
                </div>
              )}

              {/* Control buttons */}
              <div className="grid grid-cols-5 gap-2">
                {['start', 'stop', 'restart', 'cleanup', 'ping'].map(action => {
                  const styles = {
                    start: { bg: 'bg-green-600/20 hover:bg-green-600/40', text: 'text-green-400', label: 'START' },
                    stop: { bg: 'bg-red-600/20 hover:bg-red-600/40', text: 'text-red-400', label: 'STOP' },
                    restart: { bg: 'bg-yellow-600/20 hover:bg-yellow-600/40', text: 'text-yellow-400', label: 'RESTART' },
                    cleanup: { bg: 'bg-orange-600/20 hover:bg-orange-600/40', text: 'text-orange-400', label: 'CLEAN' },
                    ping: { bg: 'bg-blue-600/20 hover:bg-blue-600/40', text: 'text-blue-400', label: 'PING' },
                  };
                  const s = styles[action];
                  const loading = workerActionLoading === `10.0.0.2-${action}`;
                  return (
                    <button key={action} onClick={() => onWorkerAction('10.0.0.2', action)}
                      disabled={loading}
                      className={`${s.bg} ${s.text} px-2 py-1.5 rounded-lg text-xs font-bold transition disabled:opacity-50`}>
                      {loading ? '...' : s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Worker offline state */}
        {celeryWorkers.filter(w => !w.is_master).length === 0 && (
          <div className="bg-black/30 rounded-xl p-3 border border-gray-700/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div>
                  <div className="text-gray-400 font-medium text-sm">PM-WORKER01</div>
                  <div className="text-gray-600 text-xs">10.0.0.2 - offline</div>
                </div>
              </div>
              <button onClick={() => onWorkerAction('10.0.0.2', 'start')}
                disabled={workerActionLoading === '10.0.0.2-start'}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-xs font-bold transition disabled:opacity-50">
                {workerActionLoading === '10.0.0.2-start' ? '...' : 'START'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
